import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaTrash, FaBox, FaDollarSign, FaCalendarAlt, FaUsers, FaSnowflake, FaCut } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CompCreateProv from './createProv';
import { useAuth } from '../context/AuthContext';

const LabelInput = React.memo(({ label, icon, ...props }) => (
  <div className="mb-6">
    <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
      {icon && <span className="mr-2 text-blue-500">{icon}</span>}
      {label}
    </label>
    <input
      {...props}
      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-white"
    />
  </div>
));

const SectionCard = ({ title, children, className = "" }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
      {title}
    </h3>
    {children}
  </div>
);

function CompCrearProducto() {
  const { token } = useAuth();
  const [proveedores, setProveedores] = useState([]);
  const [showCreateProvModal, setShowCreateProvModal] = useState(false);
  const [productosAll, setProductosAll] = useState([]);
  const [productosPadres, setProductosPadres] = useState([]);

  const [searchParams] = useSearchParams()
  const plug = searchParams.get('plug')
  const name = searchParams.get('name')

  const [formData, setFormData] = useState({
    nombre_producto: name ? name : '',
    precio: '',
    precio_compra: '',
    precio_mayorista_a: '',
    precio_mayorista_b: '',
    precio_mayorista_c: '',
    codigo_barras: plug ? [plug] : [''],
    fecha_vencimiento: '',
    stock_unidades: '',
    stock_deposito: '',
    proveedores_ids: [],
    es_refrigerado: false,
    fraccionable: false,
    combinable: false,
    cantidad_pallets: '',
    cantidad_bultos: '',
    producto_padre_id: null,
    peso_unitario: '',
    // peso_unitario: '', // Comentado - usando fraccion_numerador/denominador
    stock_fraccionado_restante: '',
    fraccion_numerador: 1,
    fraccion_denominador: 1,
    busquedaPadre: '',
  });

  useEffect(() => {
    const fetchProvs = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL}/proveedores`,
          {
            headers: {
              'x-auth-token': token,
            },
          }
        );
        setProveedores(response.data.proveedores);
      } catch (error) {
        console.error('Error al obtener los proveedores:', error);
        alert('No se pudieron cargar los proveedores.');
      }
    };
    fetchProvs();
  }, [showCreateProvModal, token]);

  // Cargar todos los productos para la búsqueda de producto padre
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/productos/admin`, {
          headers: { 'x-auth-token': token }
        });
        setProductosAll(response.data.productos);

        // Cargar productos padres disponibles
        const padresResponse = await axios.get(`${import.meta.env.VITE_API_URL}/productos/padres`, {
          headers: { 'x-auth-token': token }
        });
        setProductosPadres(padresResponse.data.productos_padres);
      } catch (error) {
        console.error('Error al cargar productos:', error);
      }
    };
    fetchProducts();
  }, [token]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCodigoReferenciaChange = (index, value) => {
    const updatedCodigos = [...formData.codigo_barras];
    updatedCodigos[index] = value;
    setFormData((prev) => ({ ...prev, codigo_barras: updatedCodigos }));
  };

  const addCodigoReferencia = () => {
    setFormData((prev) => ({
      ...prev,
      codigo_barras: [...prev.codigo_barras, ''],
    }));
  };

  const deleteCodigoReferencia = (index) => {
    const updatedCodigos = formData.codigo_barras.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, codigo_barras: updatedCodigos }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/productos`,
        formData,
        {
          headers: {
            'x-auth-token': token,
          },
        }
      );
      setFormData({
        nombre_producto: '',
        precio: '',
        precio_compra: '',
        precio_mayorista_a: '',
        precio_mayorista_b: '',
        precio_mayorista_c: '',
        codigo_barras: [''],
        fecha_vencimiento: '',
        stock_unidades: '',
        stock_deposito: '',
        proveedores_ids: [],
        es_refrigerado: false,
        fraccionable: false,
        combinable: false,
        cantidad_pallets: '',
        cantidad_bultos: '',
        producto_padre_id: null,
        peso_unitario: '',
        // peso_unitario: '', // Comentado - usando fraccion_numerador/denominador
        stock_fraccionado_restante: '',
        fraccion_numerador: 1,
        fraccion_denominador: 1,
        busquedaPadre: '',
      });
      alert('Producto creado exitosamente.');
    } catch (error) {
      console.error('Error al crear el producto:', error);
      alert('Ocurrió un error al crear el producto.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Crear Producto</h1>
          <p className="text-gray-600">Completa la información del nuevo producto</p>
        </div>

        <motion.form 
          onSubmit={handleSubmit} 
          className="space-y-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Información Básica */}
          <SectionCard title="📋 Información Básica">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <LabelInput
                required
                label="Nombre del Producto"
                type="text"
                name="nombre_producto"
                value={formData.nombre_producto}
                onChange={handleChange}
                placeholder="Ej: Coca Cola 500ml"
              />

              <LabelInput
                required
                label="Cantidad de Unidades"
                icon={<FaBox />}
                type="number"
                name="stock_unidades"
                value={formData.stock_unidades}
                onChange={handleChange}
                step="0.001"
                placeholder="Ej: 100.240"
              />

              <LabelInput
                label="Stock en Depósito"
                icon={<FaBox />}
                type="number"
                name="stock_deposito"
                value={formData.stock_deposito}
                onChange={handleChange}
                step="0.001"
                placeholder="Ej: 50.120"
              />

              <LabelInput
                required
                label="Cantidad por Bulto"
                icon={<FaBox />}
                type="number"
                name="cantidad_bultos"
                value={formData.cantidad_bultos}
                onChange={handleChange}
                step="0.001"
                placeholder="Ej: 12.500"
              />

              <LabelInput
                label="Bultos por Pallet"
                icon={<FaBox />}
                type="number"
                name="cantidad_pallets"
                value={formData.cantidad_pallets}
                onChange={handleChange}
                step="0.001"
                placeholder="Ej: 20.750"
              />
            </div>
          </SectionCard>

          {/* Códigos de Barras */}
          <SectionCard title="🏷️ Códigos de Barras">
            <div className="space-y-3">
              {formData.codigo_barras.map((codigo, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={codigo}
                    onChange={(e) => handleCodigoReferenciaChange(index, e.target.value)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Plug asociado"
                  />
                  <button
                    type="button"
                    onClick={() => deleteCodigoReferencia(index)}
                    className="px-4 py-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FaTrash />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addCodigoReferencia}
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <FaPlus className="mr-2" /> Agregar Código
              </button>
            </div>
          </SectionCard>

          {/* Precios */}
          <SectionCard title="💰 Gestión de Precios">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <LabelInput
                required
                label="Precio de Venta"
                icon={<FaDollarSign />}
                type="number"
                name="precio"
                value={formData.precio}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />

              <LabelInput
                required
                label="Precio sin IVA"
                icon={<FaDollarSign />}
                type="number"
                name="precio_compra"
                value={formData.precio_compra}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />

              <LabelInput
                label="Precio Mayorista A"
                icon={<FaDollarSign />}
                type="number"
                name="precio_mayorista_a"
                value={formData.precio_mayorista_a}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />

              <LabelInput
                label="Precio Mayorista B"
                icon={<FaDollarSign />}
                type="number"
                name="precio_mayorista_b"
                value={formData.precio_mayorista_b}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />

              <LabelInput
                label="Precio Mayorista C"
                icon={<FaDollarSign />}
                type="number"
                name="precio_mayorista_c"
                value={formData.precio_mayorista_c}
                onChange={handleChange}
                placeholder="0.00"
                step="0.01"
              />

              <LabelInput
                label="Fecha de Vencimiento"
                icon={<FaCalendarAlt />}
                type="date"
                name="fecha_vencimiento"
                value={formData.fecha_vencimiento}
                onChange={handleChange}
              />
            </div>
          </SectionCard>

          {/* Proveedores */}
          <SectionCard title="🏢 Proveedores">
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {proveedores.map((prov) => (
                <label key={prov.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    value={prov.id}
                    checked={formData.proveedores_ids.includes(prov.id)}
                    onChange={(e) => {
                      const newProveedores = e.target.checked
                        ? [...formData.proveedores_ids, prov.id]
                        : formData.proveedores_ids.filter((id) => id !== prov.id);
                      setFormData((prev) => ({ ...prev, proveedores_ids: newProveedores }));
                    }}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">{prov.nombre}</span>
                    <div className="flex gap-2 mt-1">
                      {prov.abreviatura && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {prov.abreviatura}
                        </span>
                      )}
                      {prov.codigo && (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                          Código: {prov.codigo}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowCreateProvModal(true)}
              className="mt-4 inline-flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              <FaPlus className="mr-2" /> Agregar Proveedor
            </button>
          </SectionCard>

          {/* Características del Producto */}
          <SectionCard title="⚙️ Características del Producto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  name="es_refrigerado"
                  checked={formData.es_refrigerado}
                  onChange={handleChange}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                />
                <div className="flex items-center">
                  <FaSnowflake className="text-blue-500 mr-2" />
                  <span className="font-medium text-gray-700">Refrigerado</span>
                </div>
              </label>

              <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  name="fraccionable"
                  checked={formData.fraccionable}
                  onChange={handleChange}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                />
                <div className="flex items-center">
                  <FaCut className="text-green-500 mr-2" />
                  <span className="font-medium text-gray-700">Fraccionable</span>
                </div>
              </label>
            </div>

            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-medium">💡 Nota:</span> Las combinaciones de productos se configuran desde la edición del producto una vez creado.
              </p>
            </div>
          </SectionCard>

          {/* Configuración Fraccionable */}
          {formData.fraccionable && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <SectionCard title="⚖️ Configuración de Producto Fraccionable" className="border-l-4 border-l-green-400">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Producto Padre
                    </label>
                    <input
                      type="text"
                      placeholder="Buscar por nombre o código..."
                      value={formData.busquedaPadre || ''}
                      onChange={e => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          busquedaPadre: value
                        }));
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      autoComplete="off"
                    />
                    {formData.busquedaPadre && (
                      <ul className="absolute z-10 bg-white border border-gray-300 rounded-lg w-full max-h-48 overflow-y-auto shadow-lg mt-1">
                        {productosAll
                          .filter(p =>
                            p.fraccionable && // Solo productos fraccionables
                            !p.producto_padre_id && // Solo productos padre (no hijos)
                            (p.nombre_producto.toLowerCase().includes(formData.busquedaPadre.toLowerCase()) ||
                            (p.codigo_barras && p.codigo_barras.some(cb =>
                              cb.codigo && cb.codigo.toLowerCase().includes(formData.busquedaPadre.toLowerCase())
                            )))
                          )
                          .slice(0, 10)
                          .map(p => (
                            <li
                              key={p.id}
                              className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  producto_padre_id: p.id,
                                  busquedaPadre: `${p.nombre_producto}${p.codigo_barras && p.codigo_barras.length > 0 ? ` (${p.codigo_barras.map(cb => cb.codigo).join(", ")})` : ""}`
                                }));
                              }}
                            >
                              <div className="flex justify-between items-center">
                                <span className="text-gray-900">
                                  {p.nombre_producto}
                                  {p.codigo_barras && p.codigo_barras.length > 0 && (
                                    <span className="text-sm text-gray-500 ml-2">
                                      ({p.codigo_barras.map(cb => cb.codigo).join(", ")})
                                    </span>
                                  )}
                                </span>
                                {formData.producto_padre_id === p.id && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                    Seleccionado
                                  </span>
                                )}
                              </div>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>

                  {/* Comentado - Campo de peso unitario, ahora usando fraccion_numerador/denominador
                  <LabelInput
                    required
                    label="Peso Unitario (g/ml)"
                    type="number"
                    name="peso_unitario"
                    value={formData.peso_unitario}
                    onChange={handleChange}
                    min="0"
                    step="0.001"
                    placeholder="0.000"
                  />
                  */}

                  {/* Campos de fracción - solo mostrar si tiene producto padre */}
                  {formData.producto_padre_id && (
                    <>
                      {/* Contenedor compacto para campos de fracción */}
                      <div className="lg:col-span-2 flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Numerador</label>
                          <input
                            type="number"
                            name="fraccion_numerador"
                            value={formData.fraccion_numerador}
                            onChange={handleChange}
                            min="1"
                            placeholder="1"
                            className="w-full px-3 py-2 border border-gray-300 rounded text-center font-semibold focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Símbolo de división compacto */}
                        <div className="text-2xl font-bold text-gray-400">
                          /
                        </div>

                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Denominador</label>
                          <input
                            type="number"
                            name="fraccion_denominador"
                            value={formData.fraccion_denominador}
                            onChange={handleChange}
                            min="1"
                            placeholder="25"
                            className="w-full px-3 py-2 border border-gray-300 rounded text-center font-semibold focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>

                        {/* Preview compacto inline */}
                        <div className="flex-1 text-center">
                          <div className="text-xs text-gray-500 mb-1">Resultado</div>
                          <div className="text-sm font-bold text-blue-700">
                            {formData.fraccion_numerador}/{formData.fraccion_denominador}
                          </div>
                          <div className="text-xs text-gray-500">
                            {(formData.fraccion_numerador / formData.fraccion_denominador * 100).toFixed(1)}%
                          </div>
                        </div>
                      </div>

                      {/* Explicación compacta */}
                      <div className="lg:col-span-2 p-2 bg-blue-50 rounded border-l-4 border-l-blue-400">
                        <p className="text-xs text-blue-800">
                          📊 <strong>Cada {Math.floor(formData.fraccion_denominador / formData.fraccion_numerador)} unidades</strong> vendidas descuenta <strong>1 unidad</strong> del producto padre
                        </p>
                      </div>
                    </>
                  )}

                  <div className="lg:col-span-2">
                    <LabelInput
                      required
                      label="Stock Fraccionado Restante (g/ml)"
                      type="number"
                      name="stock_fraccionado_restante"
                      value={formData.stock_fraccionado_restante}
                      onChange={handleChange}
                      min="0"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </SectionCard>
            </motion.div>
          )}

          {/* Botón Submit */}
          <div className="flex justify-center pt-6">
            <button
              type="submit"
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-lg font-semibold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transform hover:scale-105 transition-all duration-200"
            >
              ✨ Crear Producto
            </button>
          </div>
        </motion.form>

        {/* Modal de Crear Proveedor */}
        {showCreateProvModal && (
          <CompCreateProv
            onClose={() => setShowCreateProvModal(false)}
            onCreateProveedor={() => {
              setShowCreateProvModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default CompCrearProducto;
